/**
 * Interview logs dashboard (API + optional localStorage fallback).
 *
 * Data rules and derived metrics live in `src/utils/viewLogsUtils.js`
 * so reviewers can evaluate logic separately from MUI layout.
 */

import { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Skeleton,
  Pagination,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { AxiosService } from "../../services/axios/axiosService";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import TabIcon from "@mui/icons-material/Tab";
import VideocamOffOutlinedIcon from "@mui/icons-material/VideocamOffOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { formatDate } from "date-fns";
import { enIN } from "date-fns/locale";
import {
  buildIntegritySummary,
  buildQuestionGroups,
  buildQuestionTextOrderMap,
  getEventCountChipLabel,
  getInterviewLogStorageKey,
  getTimelineDotColorHex,
  normalizeInterviewLogRow,
  safeJsonParse,
  sortLogsByTimestampAsc,
} from "../../utils/viewLogsUtils";

const QUESTIONS_PER_PAGE = 5;

/**
 * Shared interview log dashboard (same UI for modal and full page).
 * @param {boolean} active — when false, skips fetch (e.g. modal closed).
 */
export function ViewLogsDashboard({ active, interviewId, candidateName }) {
  const theme = useTheme();
  const [loading, setLoading] = useState(() => !!active && !!interviewId);
  const [logs, setLogs] = useState([]);
  const [questionPage, setQuestionPage] = useState(1);

  /** Fetch from API, then fall back to browser storage (same key as interview page). */
  useEffect(() => {
    if (!active || !interviewId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);

      try {
        const res = await AxiosService.get(`/api/EventLogs/${interviewId}`);
        const rows = Array.isArray(res) ? res : res?.logs;
        if (Array.isArray(rows)) {
          if (!cancelled) {
            setLogs(rows.map((apiRow) => normalizeInterviewLogRow(apiRow, interviewId)));
            setLoading(false);
          }
          return;
        }
      } catch {
        /* API failed — try local cache below */
      }

      const logKey = getInterviewLogStorageKey(interviewId);
      const local = logKey ? safeJsonParse(localStorage.getItem(logKey)) : null;
      if (Array.isArray(local)) {
        if (!cancelled) {
          setLogs(local.map((storedRow) => normalizeInterviewLogRow(storedRow, interviewId)));
          setLoading(false);
        }
        return;
      }

      if (!cancelled) {
        setLogs([]);
        setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [active, interviewId]);

  const sortedLogs = useMemo(() => sortLogsByTimestampAsc(logs), [logs]);

  /** When `questionNum` is 0/missing, order questions by first-seen `questionText`. */
  const questionTextOrderMap = useMemo(() => buildQuestionTextOrderMap(sortedLogs), [sortedLogs]);

  const summary = useMemo(
    () => buildIntegritySummary(sortedLogs, questionTextOrderMap),
    [sortedLogs, questionTextOrderMap],
  );

  const questionGroups = useMemo(
    () => buildQuestionGroups(sortedLogs, questionTextOrderMap),
    [sortedLogs, questionTextOrderMap],
  );

  //useMemo to get question page count
  const questionPageCount = useMemo(() => {
    if (loading) return 1;
    return Math.max(1, Math.ceil(questionGroups.length / QUESTIONS_PER_PAGE));
  }, [questionGroups.length, loading]);

  //useMemo to get paged question groups
  const pagedQuestionGroups = useMemo(() => {
    if (loading) return [];
    if (questionGroups.length <= QUESTIONS_PER_PAGE) return questionGroups;
    const start = (questionPage - 1) * QUESTIONS_PER_PAGE;
    return questionGroups.slice(start, start + QUESTIONS_PER_PAGE);
  }, [questionGroups, questionPage, loading]);

  //useEffect to reset question page
  useEffect(() => {
    setQuestionPage(1);
  }, [interviewId]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3, md: 2 },
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        width: "100%",
        boxSizing: "border-box",
        minWidth: 0,
        minHeight: !loading && summary.totalEvents === 0 ? 420 : undefined,
      }}
    >
      <>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", md: "center" }}
            sx={{ mb: 2 }}
          >
            <Box sx={{ flex: 1, minWidth: 240 }}>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
          Candidate Name : <span style={{ fontWeight: 900,fontSize: 16, textTransform: 'capitalize' }}>{candidateName}</span>
        </Typography>
       
       
            </Box>
            {/* <Typography variant="body2" color="text.secondary">
          Interview Date: <span style={{ fontWeight: 700,fontSize: 16 }}>{summary.}</span>
        </Typography> */}
            
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} >
            {[
              {
                label: "TOTAL FLAGS",
                value: summary.totalFlags,
                sub: "Flagged events only",
                dot: "#ed3636"  ,
                Icon: FlagOutlinedIcon,
              },
              {
                label: "PAGE RELOADS",
                value: summary.reloads,
                sub: "Across all questions",
                dot:"#ed883d",
                Icon: RefreshIcon,
              },
              {
                label: "TAB SWITCHES",
                value: summary.tabSwitches,
                sub: "Away + Return",
                dot: "#882dcb",
                Icon: TabIcon,
              },
              {
                label: "DEVICE ISSUES",
                value: summary.cameraIssues,
                sub: "Revoked mid-interview",
                dot: '#3d72efe8',
                Icon: VideocamOffOutlinedIcon,
              },
              {
                label: "INTERVIEW STATUS",
                value: "Completed",
                sub: summary.timeRange,
                dot: "#22c55e",
                Icon: CheckCircleOutlineIcon,
              },
            ].map((kpiCard) => {
              const KpiIcon = kpiCard.Icon;
              return (
              <Paper
                key={kpiCard.label}
                elevation={0}
                sx={{
                  flex: 1,
                  p: 2,
                  borderRadius: 2.5,
                  border: `1px solid ${theme.palette.divider}`,
                  background: theme.palette.mode === "dark"
                    ? "linear-gradient(180deg, rgba(2,6,23,0.5), rgba(15,23,42,0.35))"
                    : "linear-gradient(180deg, #ffffff, #fbfdff)",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  minHeight: 110,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.2 }}>
                  {loading ? (
                    <Skeleton variant="rounded" width={20} height={20} sx={{ borderRadius: 0.75, flexShrink: 0 }} />
                  ) : KpiIcon ? (
                    <KpiIcon sx={{ fontSize: 20, color: kpiCard.dot, flexShrink: 0 }} aria-hidden />
                  ) : null}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 800, letterSpacing: 0.5 }}
                  >
                    {kpiCard.label}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                  {loading ? (
                    <Skeleton width={80} height={38} />
                  ) : kpiCard.label === "INTERVIEW STATUS" ? (
                    <Chip
                      label={kpiCard.value}
                      size="small"
                      sx={{
                        height: 22,
                        fontWeight: 800,
                        bgcolor: "rgba(34,197,94,0.15)",
                        color: "#15803d",
                      }}
                    />
                  ) : (
                    <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                      {kpiCard.value}
                    </Typography>
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75 }}>
                  {loading ? <Skeleton width="70%" /> : kpiCard.sub}
                </Typography>
              </Paper>
            );
            })}
          </Stack>
        <Divider sx={{ my: 1.5 }} />
          {/* Empty-state banner: show after candidate + KPI cards when there are zero logs */}
          {!loading && summary.totalEvents === 0 && (
            <Paper
              elevation={0}
              sx={{
                mt: 1.5,
                mb: 1,
                p: 2,
                borderRadius: 2,
                border: `1px dashed ${theme.palette.divider}`,
                backgroundColor: "rgba(148,163,184,0.05)",
              }}
            >
              <Typography variant="body2" color="text.secondary" fontWeight={700}>
                No logs found for this interview.
              </Typography>
            </Paper>
          )}

          {/* Only show flagged events + question breakdown when we actually have logs */}
          {summary.totalEvents > 0 && (
            <>
              <Typography variant="overline" sx={{ fontWeight: 900, color: "text.secondary", letterSpacing: 0.8 }}>
                QUESTION BREAKDOWN
              </Typography>

              <Stack spacing={1.25} sx={{ mt: 1.25, width: "100%", minWidth: 0 }}>
                {loading ? (
                  <>
                    {[1, 2, 3].map((index) => (
                      <Paper
                        key={index}
                        elevation={0}
                        sx={{ p: 2, borderRadius: 2.5, border: `1px solid ${theme.palette.divider}` }}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Skeleton variant="circular" width={30} height={30} />
                          <Box sx={{ flex: 1 }}>
                            <Skeleton width="60%" />
                            <Skeleton width="30%" />
                          </Box>
                        </Stack>
                      </Paper>
                    ))}
                  </>
                ) : questionGroups.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    No events to show.
                  </Typography>
                ) : (
                  pagedQuestionGroups.map((questionGroup) => (
                <Accordion
                  key={questionGroup.q === -1 ? "feedback" : questionGroup.q === 0 ? "unassigned" : questionGroup.q}
                  disableGutters
                  elevation={0}
                  sx={{
                    borderRadius: 2.5,
                    border: `1px solid ${theme.palette.divider}`,
                    "&:before": { display: "none" },
                    overflow: "hidden",
                    width: "100%",
                    mx: 0,
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "rgba(148,163,184,0.06)" }}>
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: "100%" }}>
                      <Chip
                        label={questionGroup.q === -1 ? "F" : questionGroup.q === 0 ? "—" : questionGroup.q}
                        size="small"
                        sx={{
                          minWidth: 30,
                          height: 30,
                          borderRadius: 999,
                          fontWeight: 900,
                          bgcolor:
                            questionGroup.q === -1
                              ? "rgba(79,70,229,0.10)"
                              : questionGroup.q === 0
                                ? "rgba(148,163,184,0.12)"
                                : "rgba(59,130,246,0.10)",
                          color:
                            questionGroup.q === -1
                              ? "#4f46e5"
                              : questionGroup.q === 0
                                ? "text.secondary"
                                : "#1d4ed8",
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 500,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {questionGroup.q === -1
                            ? "Feedback"
                            : questionGroup.q === 0
                              ? "Other events (no question on log)"
                              : questionGroup.questionText || `Question ${questionGroup.q}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" >
                        {questionGroup.rows.length} events
                        </Typography>
                        {/* <Typography variant="caption" color="text.secondary" >
                          {questionGroup.duration ? `${questionGroup.duration}  ·  ` : ""}{questionGroup.rows.length} events
                        </Typography> */}
                      </Box>
                      <Chip
                        label={`${questionGroup.flagCount || 0} flags`}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          bgcolor: questionGroup.flagCount > 0 ? "rgba(245,158,11,0.12)" : "rgba(16,185,129,0.12)",
                          color: questionGroup.flagCount > 0 ? "#b45309" : "#047857",
                        }}
                      />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 2 }}>
                    <Stack spacing={1}>
                      {questionGroup.rows.map((logEntry, rowIndex) => (
                        <Stack
                          key={`${logEntry.timestamp}-${rowIndex}`}
                          direction="row"
                          spacing={1.25}
                          alignItems="flex-start"
                          sx={{ minWidth: 0 }}
                        >
                          {/* Dot + connector line */}
                          <Box
                            sx={{
                              width: 14,
                              flex: "0 0 14px",
                              mt: 2,
                              alignSelf: "stretch",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                            }}
                          >
                            <Box
                              sx={{
                                mt: 0.5,
                                width: 8,
                                height: 8,
                                borderRadius: 999,
                                bgcolor: getTimelineDotColorHex(logEntry.eventName),
                              }}
                            />
                            {rowIndex < questionGroup.rows.length - 1 && (
                              <Box
                                sx={{
                                  width: 1.5,
                                  flex: 1,
                                  mt: 0.5, // gap from dot                                  
                                  bgcolor: "rgba(148,163,184,0.55)",
                                  borderRadius: 999,
                                  minHeight: 11,
                                }}
                              />
                            )}
                          </Box>
                          <Typography variant="caption" sx={{ flex: 1, minWidth: 0 }}>
                            {String(logEntry.eventName || "-").replaceAll("_", " ").toLowerCase().replace(/(^|\s)\S/g, (m) => m.toUpperCase())}
                          </Typography>
                          
                          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                            {formatDate(logEntry.timestamp, "hh:mm:ss a", { locale: enIN })}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))
                )}
              </Stack>
            </>
          )}

          {!loading && questionPageCount > 1 && summary.totalEvents > 0 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination
                count={questionPageCount}
                page={questionPage}
                onChange={(_, nextPage) => setQuestionPage(nextPage)}
                shape="rounded"
                color="primary"
                size="small"
              />
            </Box>
          )}

      </>
    </Paper>
  );
}

export default ViewLogsDashboard;
